import DataLoader from 'dataloader'
import { GraphQLError } from 'graphql'

import { CaseStatus, CaseVersion, Maybe, Mortgage, MortgageProduct, MortgageWithProduct, PropertyVersion } from '../generated/resolvers'
import request from '../requesters/default'
import {
  CdmGetPropertiesSummaryResponse,
} from '../service/luther/model'
import { formatClient } from '../utils/schemaMapping/client'
import { formatMortgage } from '../utils/schemaMapping/mortgage'
import { formatProperty } from '../utils/schemaMapping/property'

export const getSummary = async (id: string, kind: 'case' | 'client', include_calculated_values: Maybe<boolean>, include_source_response: Maybe<boolean>) => {
  const response = await request.get<CdmGetPropertiesSummaryResponse>('/property/summary', {
    params: {
      ...(kind === 'client' ? { client_id: id } : { case_id: id }),
      include_calculated_values,
      include_source_response
    },
  })

  if (!response?.data?.summary) {
    throw new GraphQLError('No summary found', {
      extensions: {
        code: 'NOT_FOUND',
        status: 404,
        id,
      },
    })
  }

  const properties = response.data.summary.properties?.map((property) => {
    if (!property?.property_id) {
      throw new GraphQLError('Property ID is missing', {
        extensions: {
          code: 'PROPERTY_ID_MISSING',
          status: 400,
          id: property.details?.property_id || 'unknown',
        },
      })
    }

    if (!property?.details) {
      throw new GraphQLError('Property details are missing', {
        extensions: {
          code: 'PROPERTY_DETAILS_MISSING',
          status: 400,
          id: property.property_id,
        },
      })
    }

    return formatProperty(property)
  })

  const mortgages = response.data.summary.mortgages?.map(({ mortgage, mortgage_id, mortgage_product, product_id }) => {
    if (!mortgage_id) {
      throw new GraphQLError('Mortgage ID is missing', {
        extensions: {
          code: 'MORTGAGE_ID_MISSING',
          status: 400,
          id: mortgage?.mortgage_id || 'unknown',
        },
      })
    }

    const parsedProduct = { ...mortgage_product, id: product_id } as MortgageProduct
    const parsedMortgage = mortgage ? { ...formatMortgage(mortgage), mortgage_product: parsedProduct } : null

    return {
      mortgage_id,
      mortgage: parsedMortgage,
      product_id,
      mortgage_product,
    }
  }) as MortgageWithProduct[]

  const clients = response.data.summary.clients?.map((client) => {
    if (!client.client_id) {
      throw new GraphQLError('Client ID is missing', {
        extensions: {
          code: 'CLIENT_ID_MISSING',
          status: 400,
          id: client.client_id || 'unknown',
        },
      })
    }

    return formatClient(client)
  })

  return {
    ...response.data.summary,
    id: id,
    properties: properties,
    mortgages: mortgages,
    clients: clients
  }
}

interface SummaryKey {
  id: string,
  kind: 'case' | 'client',
  include_calculated_values: Maybe<boolean>,
  include_source_response: Maybe<boolean>
}

const batchSummaries = async (keys: readonly SummaryKey[]) => {
  const results = await Promise.all(
    keys.map(async ({ id, kind, include_calculated_values, include_source_response }) =>
      await getSummary(id, kind, include_calculated_values, include_source_response)
    )
  )

  // If a key does not return a result, we return an error for that key, return the results in the same order as the keys
  // https://github.com/graphql/dataloader#batch-function
  return keys.map(key => results?.find(result => result.id === key.id) || new GraphQLError(`No result for ${key.id}`, {
    extensions: {
      status: 404,
    },
  }))
}

export const SummaryLoader = new DataLoader(batchSummaries, {
  cacheKeyFn: (key: SummaryKey) => `${key.id}-${key.kind}-${key.include_calculated_values}-${key.include_source_response}`
})

export const mapPropertyPortfolio = (properties?: PropertyVersion[], mortgages?: Maybe<Mortgage>[], status?: Maybe<CaseStatus>) => {
  // Attach secured mortgages to each property
  const mappedProperties = properties?.map((property) => {
    const securedMortgages = mortgages?.filter(
      (mortgage) => mortgage?.property_secured_ids?.includes(property.property_id)
    )
    return {
      ...property,
      details: {
        ...property.details,
        mortgages: securedMortgages,
        case_status: status,
      },
    }
  })

  return mappedProperties ?? []
}

export const fetchSummaryEntitiesAndMapToCase = async (caseResult: CaseVersion) => {
  // Using a DataLoader to batch summary requests with the same parameters, helpes to reduce duplicate requests which can harm performance
  const result = await SummaryLoader.load({
    id: caseResult.id,
    kind: 'case',
    include_calculated_values: false,
    include_source_response: false
  })

  if (result instanceof GraphQLError) {
    throw result
  }

  const { properties, mortgages } = result

  // Extract mortgage from MortgageWithProduct response, the product has already nested inside the mortgage entity
  const mappedMortgages = mortgages?.map(({ mortgage }) => mortgage) ?? []

  // Attach secured mortgages to each property
  const mappedProperties = mapPropertyPortfolio(properties, mappedMortgages, caseResult?.details?.status)

  return {
    ...caseResult,
    details: {
      ...caseResult.details,
      property_portfolio: mappedProperties as PropertyVersion[],
      mortgages: mappedMortgages as Mortgage[]
    }
  }
}